import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '@cycle-hangars/routes/Routes';
import { useCycleStore } from '@cycle-hangars/store/store';
import { ZodError } from 'zod';
import { ErrorMessages } from '@types';
import {
  scrollTop,
  parseZodErrors,
  stringFieldSchema,
  textAreaInputSchema,
} from '@utils';
import {
  SELECT_OPTIONS_ERROR,
  VALID_LOCK_CODE_ERROR,
} from '@cycle-hangars/constants/errorMessages';
import {
  NavigationButtons,
  RadioOptions,
  TextInputMolecule,
} from '@molecules/*';
import { ErrorSummary } from '@atoms/*';

export const LockCodeDetials = () => {
  const {
    doesCodeMatch,
    knowsLockCode,
    updateKnowsLockCode,
    lockCode,
    updateLockCode,
  } = useCycleStore();
  const navigate = useNavigate();
  const MAX_LENGTH = 10;
  const [textInput, setTextInput] = useState(lockCode);
  const [result, setResult] = useState(knowsLockCode);
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const radioOptionTitle = 'Do you know the lock code? ';
  const radioOptionsDescription =
    'This is usually found on the key and lock of the cycle hangar.';
  const textInputLabel = 'Enter lock code';
  const textInputDescription =
    'This is a mix of numbers and letters, up to 10 characters.';
  const lockCodeInput = textInput?.toUpperCase().replaceAll(' ', '');

  const handleChangeBinary = (option: string) => {
    setResult(option as 'Yes' | 'No');
    setErrorMessages([]);
  };

  const handleChangeCodeInput = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const sanitisedUserInput = event.target.value.replace(
      /[^a-zA-Z0-9\s]/g,
      '',
    );
    setTextInput(sanitisedUserInput);
    setErrorMessages([]);
  };

  const buttonNext = {
    onClick: async () => {
      try {
        const validatedBinaryData = stringFieldSchema(
          'knowsLockCode',
          SELECT_OPTIONS_ERROR.required.message,
        ).parse({
          knowsLockCode: result,
        });
        if (result === 'Yes') {
          const validateCodeInput = textAreaInputSchema({
            fieldName: 'lockCode',
            requiredMessage: VALID_LOCK_CODE_ERROR.required.message,
            maxLength: MAX_LENGTH,
            maxLengthMessage: VALID_LOCK_CODE_ERROR.required.message,
          }).parse({
            lockCode: textInput,
          });
          const result = lockCodeInput.length > 0;
          if (!result) {
            setErrorMessages([
              {
                path: 'lockCode',
                message: VALID_LOCK_CODE_ERROR.required.message,
              },
            ]);
            return;
          }
          if (validateCodeInput.lockCode) {
            updateLockCode(validateCodeInput.lockCode);
          }
        }
        if (validatedBinaryData.knowsLockCode) {
          updateKnowsLockCode(
            validatedBinaryData.knowsLockCode as 'Yes' | 'No',
          );
        }
        if (result === 'Yes') {
          navigate(RoutesList.TELL_US_MORE);
        } else if (result === 'No') {
          navigate(RoutesList.TELL_US_MORE);
        }
        scrollTop();
      } catch (error) {
        const validationErrors = parseZodErrors(error as ZodError);
        setErrorMessages(
          validationErrors.map((error: ErrorMessages) => ({
            ...error,
            href: error.path,
          })),
        );
      }
    },
  };

  const buttonPrevious = {
    onClick: () => {
      if (doesCodeMatch === 'Yes') {
        navigate(RoutesList.KEY_TURN);
      } else {
        navigate(RoutesList.CODE_MATCH);
      }
      scrollTop();
    },
  };

  const options = [
    {
      labelText: 'Yes',
      isDefault: knowsLockCode === 'Yes',
    },
    {
      labelText: 'No',
      isDefault: knowsLockCode === 'No',
    },
  ];

  return (
    <div>
      <div className={' mt-6'}>
        <ErrorSummary messages={errorMessages} />
      </div>

      <h1 className="text-brand-primary body-2 text-h2 mb-8 mt-[2.75rem]">
        Lock code details
      </h1>

      <RadioOptions
        title={radioOptionTitle}
        description={radioOptionsDescription}
        options={options}
        onChange={handleChangeBinary}
        errorPath={'knowsLockCode'}
        errorMessages={errorMessages}
      />

      {result === 'Yes' ? (
        <TextInputMolecule
          label={textInputLabel}
          description={textInputDescription}
          name="lockCode"
          itemID="lockCode"
          disabled={false}
          value={textInput}
          type="text"
          onChange={handleChangeCodeInput}
          errorPath={'lockCode'}
          errorMessages={errorMessages}
          maxLength={MAX_LENGTH}
        />
      ) : null}

      <NavigationButtons
        buttonNext={buttonNext}
        buttonPrevious={buttonPrevious}
      />
    </div>
  );
};
